/**
 * The inbox left rail's vocabulary, mirroring
 * `PhoenixCapital.Core.Constants.CommunicationInboxScopes`.
 *
 * The SERVER owns the rule: the page sends whatever scope the URL holds and
 * the service normalises it. This mirror exists for one reason — the rail has
 * to know which item to highlight, and highlighting `my-recent` as nothing, or
 * `compliance` as nothing for a regular caller, would leave the page with no
 * selected item and no explanation. It is deliberately the only client-side
 * copy, and `inboxScopes.parity.test.ts` reads the C# and fails if the two
 * drift.
 */

export const INBOX_SCOPE = {
  /** Everything not deleted. See-all callers only. */
  All: "all",
  /** Only entries the caller logged. The default for a regular caller. */
  Mine: "mine",
  /** Entries carrying exactly one placement. */
  Unplaced: "unplaced",
  /** Everything, soft-deleted entries included. See-all callers only. */
  Compliance: "compliance",
} as const;

export type InboxScope = (typeof INBOX_SCOPE)[keyof typeof INBOX_SCOPE];

/** The rail's original key for `mine`, still accepted so old links work. */
export const MINE_LEGACY_ALIAS = "my-recent";

const SEE_ALL_ONLY: readonly InboxScope[] = [INBOX_SCOPE.All, INBOX_SCOPE.Compliance];

/** Every scope the module recognises, alias included. Mirrors `Known`. */
export const KNOWN_SCOPES: readonly string[] = [
  INBOX_SCOPE.All,
  INBOX_SCOPE.Mine,
  INBOX_SCOPE.Unplaced,
  INBOX_SCOPE.Compliance,
  MINE_LEGACY_ALIAS,
];

/** The view a caller lands on when the URL names no scope. */
export function defaultScopeFor(canViewAll: boolean): InboxScope {
  return canViewAll ? INBOX_SCOPE.All : INBOX_SCOPE.Mine;
}

/**
 * Resolves a requested scope to the one that will be served. Absent, blank,
 * unrecognised, or see-all-only-when-the-caller-is-not all collapse to the
 * caller's default; the legacy alias collapses to `mine` for both audiences.
 */
export function normalizeScope(
  requested: string | null | undefined,
  canViewAll: boolean,
): InboxScope {
  const raw = requested?.trim().toLowerCase();
  if (!raw) return defaultScopeFor(canViewAll);

  if (raw === MINE_LEGACY_ALIAS) return INBOX_SCOPE.Mine;
  if (raw === INBOX_SCOPE.Mine) return INBOX_SCOPE.Mine;
  if (raw === INBOX_SCOPE.Unplaced) return INBOX_SCOPE.Unplaced;

  const seeAllOnly = SEE_ALL_ONLY.find((s) => s === raw);
  if (seeAllOnly) return canViewAll ? seeAllOnly : defaultScopeFor(canViewAll);

  return defaultScopeFor(canViewAll);
}
